import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toAbsoluteUrl } from "@/lib/imageUrl";
import ImageFallback from "@/components/common/ImageFallback";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";
import { HOME_WRAP, SECTION_Y } from "./homeTheme";

const imageOf = (c) => c.image || c.sample?.image || "";

/**
 * Interactive category navigator. The list on the left is the real product
 * taxonomy (only categories that contain products); hovering, focusing or
 * tapping one makes it active: its number/title turn navy with a blue marker, and
 * the panel on the right cross-fades to that category's photo, description and
 * its real subcategories (as links). On phones the list becomes a row of pills.
 */
export default function CategoryExplorer({ categories, data }) {
  const section = data?.categoryExplorerSection || {};
  const [active, setActive] = useState(0);
  const [brokenImgs, setBrokenImgs] = useState(() => new Set()); // images that failed to load: show the branded placeholder instead
  const reduce = useReducedMotion();
  if (!categories.length) return null;
  const cat = categories[Math.min(active, categories.length - 1)];
  const subs = (cat.subcategories || []).slice(0, 8);
  const img = imageOf(cat);
  const to = `/products/${cat.slug}`;

  return (
    <section className={`bg-[#F7F9FC] ${SECTION_Y}`} aria-labelledby="home-categories-title">
      <div className={HOME_WRAP}>
        <SectionHeader
          eyebrow={section.eyebrow || "Product Range"}
          title={section.title || "Explore by Category"}
          titleId="home-categories-title"
          description={section.description || "Every product line we manufacture, organised by application."}
          className="mb-12 lg:mb-16"
        />

        {/* Phone / tablet: pill tabs */}
        <div className="-mx-5 mb-6 flex gap-2 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:px-8 lg:hidden" role="tablist" aria-label="Product categories">
          {categories.map((c, i) => (
            <button
              key={c.slug}
              type="button"
              role="tab"
              aria-selected={i === active}
              onClick={() => setActive(i)}
              className={`shrink-0 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${i === active ? "border-[#071B3A] bg-[#071B3A] text-white" : "border-[#D5DCE8] bg-white text-[#13233F]"}`}
            >
              {c.title}
            </button>
          ))}
        </div>

        <div className="grid items-stretch gap-8 lg:grid-cols-[5fr_6fr] lg:gap-16">
          {/* Desktop: numbered list */}
          <Reveal className="hidden lg:block">
            <ul className="border-t border-[#DCE3EE]" onMouseLeave={() => undefined}>
              {categories.map((c, i) => {
                const on = i === active;
                return (
                  <li key={c.slug} className="border-b border-[#DCE3EE]">
                    <Link
                      to={`/products/${c.slug}`}
                      onMouseEnter={() => setActive(i)}
                      onFocus={() => setActive(i)}
                      aria-current={on ? "true" : undefined}
                      className="group relative flex items-center gap-6 py-5 pr-2 outline-none"
                    >
                      <span className={`absolute -left-4 top-1/2 h-8 w-[3px] -translate-y-1/2 rounded-full bg-[#1769FF] transition-all duration-300 ${on ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0"}`} aria-hidden="true" />
                      <span className={`w-8 font-heading text-sm tabular-nums transition-colors duration-300 ${on ? "text-[#1769FF]" : "text-[#98A2B3]"}`}>{String(i + 1).padStart(2, "0")}</span>
                      <span className={`flex-1 font-heading text-[26px] font-medium tracking-[-0.01em] transition-all duration-300 xl:text-[30px] ${on ? "translate-x-1 text-[#071B3A]" : "text-[#667085] group-hover:text-[#13233F]"}`}>{c.title}</span>
                      <ArrowRight className={`h-5 w-5 shrink-0 transition-all duration-300 ${on ? "translate-x-0 text-[#1769FF] opacity-100" : "-translate-x-2 text-[#98A2B3] opacity-0"}`} aria-hidden="true" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Reveal>

          {/* Preview panel */}
          <Reveal delay={0.1} className="min-w-0">
            <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-[#E4E9F2] bg-white shadow-[0_30px_70px_-40px_rgba(7,27,58,0.4)]">
              <div className="relative aspect-[16/11] bg-[radial-gradient(ellipse_at_50%_45%,#FFFFFF_0%,#EEF2F8_100%)] sm:aspect-[16/10]">
                <AnimatePresence mode="wait" initial={false}>
                  {img && brokenImgs.has(img) ? (
                    <ImageFallback key={`${cat.slug}-fb`} label={cat.title} className="absolute inset-0 h-full w-full" />
                  ) : img ? (
                    <motion.img
                      key={cat.slug}
                      src={toAbsoluteUrl(img)}
                      alt={`${cat.title} — Shingare product`}
                      loading="lazy"
                      decoding="async"
                      onError={() => setBrokenImgs((s) => new Set(s).add(img))}
                      initial={reduce ? false : { opacity: 0, scale: 1.04 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={reduce ? undefined : { opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0 h-full w-full object-contain p-6 mix-blend-multiply [filter:brightness(1.07)] sm:p-8"
                    />
                  ) : null}
                </AnimatePresence>
                <div className="absolute left-5 top-5 rounded-full border border-[#E4E9F2] bg-white/90 px-3 py-1 font-heading text-xs tabular-nums text-[#667085] backdrop-blur">
                  {String(Math.min(active, categories.length - 1) + 1).padStart(2, "0")} / {String(categories.length).padStart(2, "0")}
                </div>
              </div>
              <div className="flex flex-1 flex-col border-t border-[#EEF1F6] p-6 sm:p-8">
                <h3 className="font-heading text-2xl font-semibold tracking-[-0.01em] text-[#071B3A]">{cat.title}</h3>
                {cat.description && <p className="mt-3 line-clamp-3 max-w-xl text-[15px] leading-relaxed text-[#667085]">{cat.description}</p>}
                {subs.length > 0 && (
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {subs.map((s) => (
                      <li key={s.slug}>
                        <Link to={`/products/${cat.slug}/${s.slug}`} className="inline-block rounded-full bg-[#EFF4FF] px-3 py-1.5 text-[13px] font-medium text-[#1B4FC4] transition-colors hover:bg-[#1769FF] hover:text-white">{s.title}</Link>
                      </li>
                    ))}
                  </ul>
                )}
                <Link to={to} className="group mt-6 inline-flex items-center gap-2 self-start text-sm font-semibold text-[#1769FF]">
                  <span className="border-b border-transparent pb-0.5 transition-colors group-hover:border-[#1769FF]">Explore {cat.title}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
